import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import MT5Service from '../../services/MT5Service';
import { AssetOrderDto, AssetDto, MT5Position, MT5AccountInfo } from '../../services/types';
import { useQuery } from '@tanstack/react-query';
// Remove lodash.isequal import
// Define the result type for the query
interface MT5DataResult {
  marketData: AssetDto[];
  positions: MT5Position[];
  accountInfo: MT5AccountInfo | null;
}

interface MT5TradingPanelProps {
  isConnected: boolean;
}

const mt5Service = new MT5Service();

const useMT5Data = (isConnected: boolean) => {
  return useQuery<MT5DataResult | null, Error>({
    queryKey: ['mt5Data', isConnected],
    queryFn: async () => {
      if (!isConnected) return null;
      const [marketData, positions, accountInfo] = await Promise.all([
        mt5Service.getMarketData(['EURUSD', 'GBPUSD', 'XAUUSD', 'USOIL']),
        mt5Service.getOpenPositions(),
        mt5Service.getAccountInfo()
      ]);
      return { marketData, positions, accountInfo };
    },
    enabled: isConnected,
    refetchInterval: 10000, // 10 seconds
  });
};

const MT5TradingPanel: React.FC<MT5TradingPanelProps> = ({ isConnected }) => {
  const theme = useTheme();
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [orderForm, setOrderForm] = useState<Partial<AssetOrderDto>>({
    assetId: '',
    orderType: 'MARKET',
    side: 'BUY',
    quantity: 1,
    timeInForce: 'DAY'
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const { data, isFetching, refetch } = useMT5Data(isConnected);
  const marketData = (data as MT5DataResult | null)?.marketData || [];
  const positions = (data as MT5DataResult | null)?.positions || [];
  const accountInfo = (data as MT5DataResult | null)?.accountInfo || null;

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orderForm.assetId || !orderForm.quantity) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setError('');
      setSuccess('');

      const order: AssetOrderDto = {
        assetId: orderForm.assetId,
        orderType: orderForm.orderType || 'MARKET',
        side: orderForm.side || 'BUY',
        quantity: orderForm.quantity || 1,
        price: orderForm.price,
        timeInForce: orderForm.timeInForce || 'DAY'
      };

      const result = await mt5Service.executeOrder(order);
      setSuccess(`Order executed successfully! Order ID: ${result.id}`);
      
      // Reset form
      setOrderForm({
        assetId: '',
        orderType: 'MARKET',
        side: 'BUY',
        quantity: 1,
        timeInForce: 'DAY'
      });

      // Reload data
      refetch();
    } catch (error: any) {
      setError('Order execution failed: ' + error.message);
    }
  };

  const handleInputChange = (field: keyof AssetOrderDto, value: any) => {
    setOrderForm((prev: Partial<AssetOrderDto>) => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  if (!isConnected) {
    return (
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mx: { xs: -0.5, sm: 0 },
      }}>
        <CardContent sx={{ 
          textAlign: 'center', 
          py: 4,
          color: 'text.secondary'
        }}>
          <SecurityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            MT5 Not Connected
          </Typography>
          <Typography variant="body2">
            Please connect to MT5 to access trading features.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Account Information - Matching Asset Portfolio style */}
      {accountInfo && (
        <Card sx={{ 
          borderRadius: 2,
          background: 'linear-gradient(90deg, #f5a623 0%, #f7b84b 100%)',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          mx: { xs: -0.5, sm: 0 },
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            }}>
              <Box>
                <Typography variant="subtitle2" color="white" sx={{ mb: 0.5, opacity: 0.9 }}>
                  Total Portfolio Value
                </Typography>
                <Typography variant="h4" color="white" sx={{ fontWeight: 600, fontSize: { xs: '1.75rem', sm: '2rem' } }}>
                  {formatCurrency(accountInfo.balance, accountInfo.currency)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="subtitle2" color="white" sx={{ mb: 0.5, opacity: 0.9 }}>
                  Total Change
                </Typography>
                <Typography variant="h4" color="white" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  {formatPercentage(accountInfo.profit / accountInfo.balance * 100)}
                  <Typography component="span" color="white" sx={{ ml: 1, fontSize: { xs: '0.875rem', sm: '1rem' }, opacity: 0.9 }}>
                    ({formatCurrency(accountInfo.profit, accountInfo.currency)})
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Account Details Cards */}
      {accountInfo && (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2
        }}>
          <Card sx={{ 
            borderRadius: 2,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            bgcolor: 'white',
          }}>
            <CardContent sx={{ 
              p: { xs: 1.5, sm: 2 },
              '&:last-child': { pb: { xs: 1.5, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'success.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5
                }}>
                  <MoneyIcon sx={{ fontSize: 16, color: 'success.600' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Balance
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                {formatCurrency(accountInfo.balance, accountInfo.currency)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            borderRadius: 2,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            bgcolor: 'white',
          }}>
            <CardContent sx={{ 
              p: { xs: 1.5, sm: 2 },
              '&:last-child': { pb: { xs: 1.5, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5
                }}>
                  <AccountBalanceIcon sx={{ fontSize: 16, color: 'primary.600' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Equity
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                {formatCurrency(accountInfo.equity, accountInfo.currency)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            borderRadius: 2,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            bgcolor: 'white',
          }}>
            <CardContent sx={{ 
              p: { xs: 1.5, sm: 2 },
              '&:last-child': { pb: { xs: 1.5, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  bgcolor: accountInfo.profit >= 0 ? 'success.100' : 'error.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5
                }}>
                  {accountInfo.profit >= 0 ? (
                    <TrendingUpIcon sx={{ fontSize: 16, color: 'success.600' }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: 16, color: 'error.600' }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Profit/Loss
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: '1rem',
                      color: accountInfo.profit >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                {formatCurrency(accountInfo.profit, accountInfo.currency)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ 
            borderRadius: 2,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            bgcolor: 'white',
          }}>
            <CardContent sx={{ 
              p: { xs: 1.5, sm: 2 },
              '&:last-child': { pb: { xs: 1.5, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'orange.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5
                }}>
                  <SecurityIcon sx={{ fontSize: 16, color: 'orange.600' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Free Margin
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                {formatCurrency(accountInfo.freeMargin, accountInfo.currency)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Market Data */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mx: { xs: -0.5, sm: 0 },
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Market Data
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={16} /> : null}
              sx={{
                borderColor: '#f5a623',
                color: '#f5a623',
                '&:hover': {
                  borderColor: '#e69512',
                  bgcolor: 'rgba(245, 166, 35, 0.04)'
                }
              }}
            >
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Symbol</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Change</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {marketData.map((asset: AssetDto) => (
                  <TableRow key={asset.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {asset.symbol}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {asset.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                      {formatCurrency(asset.currentPrice, asset.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatPercentage(asset.dayChangePercentage)}
                        size="small"
                        color={asset.dayChange >= 0 ? 'success' : 'error'}
                        sx={{ fontSize: '0.75rem', height: '20px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                      {asset.type}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Optionally show a small overlay spinner in the corner if isLoading */}
        </CardContent>
      </Card>

      {/* Trading Form */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mx: { xs: -0.5, sm: 0 },
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Place Order
          </Typography>
        
        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            </Alert>
        )}

        {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
            {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleOrderSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2
            }}>
              <FormControl fullWidth size="small">
                <InputLabel>Symbol *</InputLabel>
                <Select
                value={orderForm.assetId}
                onChange={(e) => handleInputChange('assetId', e.target.value)}
                  label="Symbol *"
                required
              >
                  <MenuItem value="">Select Symbol</MenuItem>
                {marketData.map((asset: AssetDto) => (
                    <MenuItem key={asset.id} value={asset.id}>
                    {asset.symbol} - {asset.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Order Type</InputLabel>
                <Select
                value={orderForm.orderType}
                onChange={(e) => handleInputChange('orderType', e.target.value)}
                  label="Order Type"
                >
                  <MenuItem value="MARKET">Market</MenuItem>
                  <MenuItem value="LIMIT">Limit</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 2
            }}>
              <FormControl fullWidth size="small">
                <InputLabel>Side</InputLabel>
                <Select
                value={orderForm.side}
                onChange={(e) => handleInputChange('side', e.target.value)}
                  label="Side"
                >
                  <MenuItem value="BUY">Buy</MenuItem>
                  <MenuItem value="SELL">Sell</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Quantity *"
                type="number"
                inputProps={{ step: 0.01, min: 0.01 }}
                value={orderForm.quantity}
                onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value))}
                required
              />

            {orderForm.orderType === 'LIMIT' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Price *"
                  type="number"
                  inputProps={{ step: 0.00001, min: 0 }}
                  value={orderForm.price}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                  required={orderForm.orderType === 'LIMIT'}
                />
            )}
            </Box>

            <Button
            type="submit"
              variant="contained"
            disabled={isFetching}
              sx={{
                bgcolor: 'orange.main',
                '&:hover': { bgcolor: 'orange.dark' },
                mt: 1
              }}
          >
            {isFetching ? 'Executing Order...' : 'Place Order'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Open Positions */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mx: { xs: -0.5, sm: 0 },
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Open Positions
          </Typography>
          
        {positions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <Typography variant="body2">No open positions</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Ticket</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Volume</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Open Price</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Current Price</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Profit/Loss</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {positions.map((position: MT5Position) => (
                    <TableRow key={position.ticket} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell>
                        <Typography variant="body2">{position.ticket}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {position.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={position.type}
                          size="small"
                          color={position.type === 'BUY' ? 'success' : 'error'}
                          sx={{ fontSize: '0.75rem', height: '20px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{position.volume}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{position.openPrice}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{position.currentPrice}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: position.profit >= 0 ? 'success.main' : 'error.main'
                          }}
                        >
                        {formatCurrency(position.profit)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export { useMT5Data };
export default MT5TradingPanel; 